import type { Emitter } from "@core/events/emitter";

/**
 * `IDevSettingsModel` — the developer OVERRIDES surfaced by the AI debug
 * dialog's "Settings" tab. Each one moves a real product behaviour away
 * from what ships — most switch something OFF for a developer who does not
 * want it running; one opt-in switches an alternative ON. Either way the
 * stock value is what an ungated build gets.
 *
 * **Web-only — there is no Unity counterpart.** The desktop client has no
 * runtime developer settings at all; what it gates, it gates at build time
 * through `IAppConfig`. This is the web port's runtime equivalent, and it is
 * deliberately a sibling of {@link IDebugModeModel} rather than more members on
 * it: that model is ONE flag with one job (is this build showing developer
 * affordances at all), and it says so; this one is a growing bag of per-feature
 * switches that all hang off it.
 *
 * **The values are DEPLOYMENT-WIDE, not per-browser.** They live in the shared
 * `app-settings.json` behind `IAppSettingsModel`, so a switch flipped here is
 * what everyone using the deployment gets — this model owns any debug gate and
 * the setter guard, not the storage. Two consequences for callers: a
 * preference is not readable until that document loads (every getter serves the
 * stock default until then, and `onChanged` fires when it lands), and a write
 * can FAIL, which is reported through `IAppSettingsModel.lastError` rather than
 * by the `void` setters below.
 *
 * **Developer-only overrides fold the debug-mode gate in**, per `Campfire_Web_UI.md`'s
 * "Gating an affordance behind a flag": the only place these switches exist is
 * a tab inside a dialog that is itself only reachable in debug mode. An
 * override that outlived debug mode would strand the developer with a feature
 * switched off and the switch that would restore it gone — the same stranding
 * the rule exists to prevent, arriving from the other direction (there the
 * preference keeps a surface ON with nothing left to close it; here it keeps a
 * behaviour OFF with nothing left to re-enable it). So leaving debug mode
 * restores stock behaviour, and re-entering it restores the developer's
 * overrides — the preference is GATED, never cleared. A setting explicitly
 * defined as deployment-wide policy is the exception: its READ remains live
 * for everyone after debug mode closes, while its WRITE stays guarded because
 * the debug panel is still the only authoring surface. `hideSparkLabel` is the
 * first such setting.
 *
 * **Why an IOC model and not a module-scoped composable.** The readers are of
 * different kinds, some of them headless (models may depend on
 * `src/models/**` but never on `src/viewmodels/**`), plus the debug dialog's
 * tab. That rules out a composable outright — and a module-scoped ref would
 * additionally be duplicated by a Vite HMR module swap, splitting readers
 * across the copies (the bug `useWorkflow`'s singleton note records).
 */
export interface IDevSettingsModel {
  /**
   * Fires whenever an effective value changes — including when the debug-mode
   * gate flips, and when the shared settings document loads or is re-read,
   * either of which changes every effective value without any preference being
   * written here. Payload-free: there are few enough settings that a reader
   * re-reads what it cares about.
   */
  readonly onChanged: Emitter<void>;

  /**
   * The shared preference, ignoring the debug gate. Only interesting to the
   * settings tab itself (where the gate is on by definition, so it equals the
   * effective value) and to anything reasoning about what was persisted.
   *
   * @see isUserFacingWorkflowAuthoringEnabled
   */
  readonly userFacingWorkflowAuthoringPreference: boolean;

  /**
   * Whether the Workflow editor offers a USER-FACING authoring surface at all —
   * the WYSIWYG editor over the MD-like authored view. The effective value,
   * gate folded in.
   *
   * False makes the raw YAML document the only surface: `useWorkflow.devMode`
   * reads true whatever the header `yaml` switch says (and that switch hides,
   * having nothing left to switch to), and `WorkflowView` never constructs its
   * Tiptap editor — so no edit can route through the authored-view parse on its
   * way into the model. The document is then only ever written as YAML, by hand
   * or by Spark.
   *
   * True unless a developer has explicitly switched it off AND debug mode is
   * on: the WYSIWYG editor is what ships.
   */
  readonly isUserFacingWorkflowAuthoringEnabled: boolean;

  /**
   * No-ops outside debug mode: the switch only exists there, so storing a
   * preference the user has no affordance to undo would be the stranding case
   * above with extra steps. (Same guard on every setter below.)
   */
  setUserFacingWorkflowAuthoringEnabled(enabled: boolean): void;

  /** @see isResponsesVoiceEnabled — the stored preference, ungated. */
  readonly responsesVoicePreference: boolean;

  /**
   * Whether a non-realtime role's Responses text bridge hands its completed
   * reply to the connected realtime model as `INTERNAL SAY:` for spoken audio.
   *
   * False publishes the Responses model's text directly to the Spark feed:
   * no second realtime response and no audio. Realtime-family roles are
   * unaffected because they never use the bridge.
   *
   * True unless a developer has explicitly switched it off AND debug mode is
   * on: spoken bridged replies are what ship.
   */
  readonly isResponsesVoiceEnabled: boolean;

  /** @see setUserFacingWorkflowAuthoringEnabled — same debug-mode guard. */
  setResponsesVoiceEnabled(enabled: boolean): void;

  /** @see isSparkLabelHidden — the stored deployment-wide preference. */
  readonly hideSparkLabelPreference: boolean;

  /**
   * Whether the name tag below Spark's 3D head is hidden. This affects only
   * the `"Spark"` billboard (including its picture disc), not the head, crown,
   * composer, or accessible labels on controls that open the composer.
   *
   * **GLOBAL — not debug-gated.** A developer authors it from the debug panel,
   * but the stored value applies to everyone using the deployment after that
   * panel closes. False by default, preserving the shipped name tag.
   */
  readonly isSparkLabelHidden: boolean;

  /** @see setUserFacingWorkflowAuthoringEnabled — same debug-mode write guard. */
  setSparkLabelHidden(hidden: boolean): void;

  /** @see isCollapsibleSparkRowEnabled — the stored preference, ungated. */
  readonly collapsibleSparkRowPreference: boolean;

  /**
   * Whether the DESKTOP editor puts Spark and the tool palette in one
   * bottom-centre row as two mutually-collapsing triggers (`DesktopToolbarRow`,
   * the phone shell's arrangement ported over). The effective value, gate
   * folded in.
   *
   * **OPT-OUT — true unless a developer has explicitly switched it off AND
   * debug mode is on** (user direction, 2026-09-02; it was opt-in until then).
   * The row is now what a desktop editor ships with; switching it off restores
   * the older arrangement — the Spark prompt permanently expanded and
   * bottom-CENTRE (Unity `AiPanel.prefab`'s own placement), with the tool pill
   * floating separately in the bottom-right corner. Both layouts are live code
   * paths in `ProjectView`.
   *
   * Desktop only — the phone shell has no room for the second arrangement and
   * ignores this entirely. A RUN takes the split layout on both shells whatever
   * this says, so the switch only ever describes the EDITOR.
   */
  readonly isCollapsibleSparkRowEnabled: boolean;

  /** @see setUserFacingWorkflowAuthoringEnabled — same debug-mode guard. */
  setCollapsibleSparkRowEnabled(enabled: boolean): void;

  /** @see isSuperGizmoEnabled — the stored preference, ungated. */
  readonly superGizmoPreference: boolean;

  /**
   * Whether the Select tool uses the combined SUPER GIZMO — the
   * `Super-Gizmo-v2` prototype ported to the web viewport
   * (`gizmos/SuperGizmoController`): translation arrows, planar pads,
   * rotation arcs, free drag and uniform scale in ONE gizmo, with dimension
   * lines and a precise-entry numeric overlay after each drag. The effective
   * value, gate folded in.
   *
   * **OPT-IN — false unless a developer has explicitly switched it on AND
   * debug mode is on**, now the only setting on this tab shaped that way (the
   * `collapsibleSparkRow` precedent it was written against became opt-out on
   * 2026-09-02). The stock gizmo
   * is the standard three.js TransformControls driven by the Move / Rotate /
   * Scale mode segments; both are live code paths in `ViewportView`, which
   * swaps controllers when this flips. While the super gizmo is on, the
   * transform-mode segments hide (the gizmo combines all three modes) and
   * the options-row Reset resets the WHOLE transform.
   */
  readonly isSuperGizmoEnabled: boolean;

  /** @see setUserFacingWorkflowAuthoringEnabled — same debug-mode guard. */
  setSuperGizmoEnabled(enabled: boolean): void;

  /** @see isWebRtcRealtimeTransportEnabled — the stored preference, ungated. */
  readonly webRtcRealtimeTransportPreference: boolean;

  /**
   * Whether Spark's realtime session connects over **WebRTC** instead of the
   * WebSocket. The effective value, gate folded in.
   *
   * **OPT-OUT — true unless a developer has explicitly switched it off AND
   * debug mode is on** (the `responsesVoice` precedent). WebRTC is what ships
   * (user direction, 2026-08-28); switching it off puts the session back on
   * `AIRealtimeTransportModel`, a WebSocket carrying base64 PCM16 both ways.
   * On WebRTC the model's audio arrives as a remote `MediaStreamTrack` and only
   * non-audio events ride an `oai-events` data channel; both are
   * implementations of one `IRealtimeTransport` and nothing above that
   * interface can tell which is live.
   *
   * Read by the ORCHESTRATOR's `resolveSessionConnection()`, not by the
   * transport itself, so the choice is resolved once per connect alongside the
   * model. Flipping it mid-session reconnects, exactly as a model change does —
   * the transport is fixed for a socket's life.
   *
   * ⚠ A failed WebRTC connect falls back to the WebSocket **stickily for the
   * app session**. That is not a nicety: the media path does not traverse the
   * campfire proxy and nothing here supplies TURN, so on a UDP-blocked network
   * WebRTC can be impossible while the WebSocket is fine.
   */
  readonly isWebRtcRealtimeTransportEnabled: boolean;

  /** @see setUserFacingWorkflowAuthoringEnabled — same debug-mode guard. */
  setWebRtcRealtimeTransportEnabled(enabled: boolean): void;
}

export const DEV_SETTINGS_MODEL = Symbol.for("Campfire.DevSettingsModel");
